import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';

import { GrainArrivalChart } from '@widgets/linear-chart';
import axios from '@shared/config/axios';
import { BAG_STATUSES, BATCH_STATUSES } from '@shared/api/constants';
import { LoadingPage } from '@widgets/loading-page';

export const AdminDashboardPage = () => {
    const [bagData, setBagData] = useState([]);
    const [batchData, setBatchData] = useState([]);
    const [bagStatistics, setBagStatistics] = useState([]);
    const [batchStatistics, setBatchStatistics] = useState([]);

    const { data: bagResponse, isLoading: isLoadingBag } = useQuery({
        queryKey: ['bag'],
        queryFn: () => {
            return axios.get(`/bags`, {
                params: {
                    status: [BAG_STATUSES.IN_STOCK, BAG_STATUSES.ROASTING]
                }
            })
                .then(({ data }) => {
                    setBagData(data);
                    return data;
                });
        },
        refetchOnWindowFocus: true,
        retry: false,
    });

    const { data: batchResponse, isLoading: isLoadingBatch } = useQuery({
        queryKey: ['batch'],
        queryFn: () => {
            return axios.get(`/batches`, { params: { status: BATCH_STATUSES.IN_STOCK } })
                .then(({ data }) => {
                    setBatchData(data);
                    return data;
                });
        },
        refetchOnWindowFocus: true,
        retry: false,
    });

    useEffect(() => {
        if (bagResponse) {
            const stats = calculateBagStatistics(bagResponse);
            setBagStatistics(stats);
        }
    }, [bagResponse]);

    useEffect(() => {
        if (batchResponse) {
            const stats = calculateBatchStatistics(batchResponse);
            setBatchStatistics(stats);
        }
    }, [batchResponse]);

    const calculateBagStatistics = (data) => {
        const result = {};

        data.forEach(item => {
            const countryName = item?.country?.name || 'Unknown Country';
            const producerName = item?.producerName || 'Unknown Producer';

            if (!result[countryName]) {
                result[countryName] = {};
            }

            if (!result[countryName][producerName]) {
                result[countryName][producerName] = {
                    remainingWeight: 0,
                    totalWeight: 0,
                };
            }

            result[countryName][producerName].remainingWeight += item?.adjustedWeight || 0;
            result[countryName][producerName].totalWeight += item?.weight || 0;
        });

        return result;
    };

    const calculateBatchStatistics = (data) => {
        const result = {};

        data.forEach(item => {
            const countryName = item.bag?.country?.name || 'Unknown Country';
            const producerName = item.bag?.producerName || 'Unknown Producer';
            const roastType = item.roastType?.name?.en || 'Unknown Roast';

            if (!result[countryName]) {
                result[countryName] = {};
            }

            if (!result[countryName][producerName]) {
                result[countryName][producerName] = {
                    roasted: {},
                    totalWeight: 0,
                };
            }

            if (!result[countryName][producerName].roasted[roastType]) {
                result[countryName][producerName].roasted[roastType] = 0;
            }

            result[countryName][producerName].roasted[roastType] += item.adjustedWeight || 0;
            result[countryName][producerName].totalWeight += item.adjustedWeight || 0;
        });

        return result;
    };

    if (isLoadingBag || isLoadingBatch) return <LoadingPage/>;

    return (
        <>
            <Container
                className="no-scrollbar"
                sx={{
                    width: '100%',
                    padding: 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    overflowY: 'auto'
                }}
            >
                <Grid container spacing={2} alignItems="stretch">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ padding: 2, height: '100%' }}>
                            <Typography variant="h6">stock overview</Typography>
                            {Object.entries(bagStatistics).map(([country, producers]) => (
                                <Box key={country} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1">{country}</Typography>
                                    {Object.entries(producers).map(([producer, data]) => (
                                        <Box key={producer} sx={{ ml: 2, mb: 1 }}>
                                            <Typography variant="body1"><b>producer:</b> {producer}</Typography>
                                            <Typography variant="body2"><b>remaining
                                                amount:</b> {data.remainingWeight.toFixed(2)} kg</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ padding: 2, height: '100%' }}>
                            <Typography hidden>{batchData.length}</Typography>
                            <Typography variant="h6">batch report</Typography>
                            {Object.entries(batchStatistics).map(([country, producers]) => (
                                <Box key={country} sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1">{country}</Typography>
                                    {Object.entries(producers).map(([producer, data]) => (
                                        <Box key={producer} sx={{ ml: 2, mb: 1 }}>
                                            <Typography variant="body1"><b>producer:</b> {producer}</Typography>
                                            <Typography variant="body2"><b>total roasted weight:</b></Typography>
                                            <ul>
                                                {Object.entries(data.roasted).map(([roastType, weight]) => (
                                                    <li key={roastType}><b>{roastType}:</b> {weight.toFixed(2)} kg</li>
                                                ))}
                                            </ul>
                                            <Typography variant="body2"><b>green input
                                                weight:</b> {data.totalWeight.toFixed(2)} kg</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ padding: 2 }}>
                            <GrainArrivalChart bagData={bagData}/>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};
